import { COMMON_COLORS } from '@neteera/neteera-web-nextgen';
import { VS_METRICS } from 'src/utils/constants';
import { PropsFromRedux } from './Connector';
import {
  HR_HISTOGRAM_THRESHOLDS,
  RR_HISTOGRAM_THRESHOLDS,
} from 'src/routes/IndividualDashboard/modules/dashboard/histogram-processor';

export const HISTOGRAM_CHART_HEIGHT = 188;
export const HISTOGRAM_LABELS_HEIGHT = 15;
export const HISTOGRAM_LABELS_OFFSET = 15;

// The HISTOGRAM_BAR_MARGIN_TOP and HISTOGRAM_BAR_MARGIN_BOTTOM values
// should be equal to Margin values sent to the Histogram component
export const HISTOGRAM_BAR_MARGIN_TOP = 33;
export const HISTOGRAM_BAR_MARGIN_BOTTOM = 20;

export const HISTOGRAM_BAR_HEIGHT =
  HISTOGRAM_CHART_HEIGHT -
  HISTOGRAM_LABELS_HEIGHT -
  HISTOGRAM_LABELS_OFFSET -
  HISTOGRAM_BAR_MARGIN_TOP -
  HISTOGRAM_BAR_MARGIN_BOTTOM;

export const DEFAULT_HISTOGRAM_CHART_PROPS = {
  height: HISTOGRAM_CHART_HEIGHT,
  margin: {
    top: HISTOGRAM_BAR_MARGIN_TOP,
    right: 22,
    left: 22,
    bottom: HISTOGRAM_BAR_MARGIN_BOTTOM,
  },
};

enum METRIC_COLORS {
  hr = 'red',
  rr = 'blue',
}

enum COLOR_INTENSITY {
  LIGHT = 'Light',
  MID = 'Mid',
  DARK = 'Dark',
}

const thresholdsByMetric = {
  [VS_METRICS.HR]: HR_HISTOGRAM_THRESHOLDS,
  [VS_METRICS.RR]: RR_HISTOGRAM_THRESHOLDS,
};

const colorByMetric = {
  [VS_METRICS.HR]: METRIC_COLORS[VS_METRICS.HR],
  [VS_METRICS.RR]: METRIC_COLORS[VS_METRICS.RR],
};

const getColorIntensity = (percentage: number) => {
  if (percentage < 15) {
    return COLOR_INTENSITY.LIGHT;
  }

  if (percentage < 30) {
    return COLOR_INTENSITY.MID;
  }

  return COLOR_INTENSITY.DARK;
};

const getColor = (metric: 'hr' | 'rr', percentage: number) =>
  COMMON_COLORS[`${colorByMetric[metric]}${getColorIntensity(percentage)}`];

const computeYPosition = (currentValue: number, maxValue: number) => {
  // HISTOGRAM_BAR_HEIGHT corresponds to maxValue
  // y position is computed from the top of the screen
  const valueUntilMax = maxValue - currentValue;

  return (
    (HISTOGRAM_BAR_HEIGHT * valueUntilMax) / maxValue + HISTOGRAM_BAR_MARGIN_TOP
  );
};

export const buildHistogramChartData = (
  histogramData: PropsFromRedux['histogramData'],
  metric: 'hr' | 'rr',
) => {
  const thresholds = thresholdsByMetric[metric];
  const mainPercentages =
    histogramData[metric.toUpperCase() as 'HR' | 'RR'].mainData.histogram
      .percentage;
  const previousWeekPercentages =
    histogramData[metric.toUpperCase() as 'HR' | 'RR'].previousWeekData
      .histogram.percentage;
  const maxValue = Math.max(
    ...Object.values(mainPercentages),
    ...Object.values(previousWeekPercentages),
  );

  return thresholds.map(t => {
    const percentage = mainPercentages[t.label] ?? 0;
    const previousWeekPercentage = previousWeekPercentages[t.label] ?? 0;

    return {
      range: t.label,
      percentage,
      fill: getColor(metric, percentage),
      lineYPosition: computeYPosition(previousWeekPercentage, maxValue),
      ghost: previousWeekPercentage,
    };
  });
};
